import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCanvaAuthStatus,
  getCanvaAuthUrl,
  disconnectCanva,
  type CanvaAuthStatus,
  type CanvaEditResponse,
} from './api';
import { uploadToCanvaAsync } from '../../services/asyncJobApi';
import { useToast } from '../../hooks/useToast';
import { brandConfig } from '../../config/brands';

// 動画アップロードモーダルの状態
export interface VideoUploadModalState {
  isOpen: boolean;
  isUploading: boolean;
  canvaUrl: string | null;
  thumbnailUrl: string | null;
  progress: number; // 0-100
}

const LOADING_PAGE_HTML = `
<!DOCTYPE html>
<html>
<head>
  <title>Canvaを準備中...</title>
  <style>
    body {
      background: #1e2026;
      color: #f5f1e6;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      margin: 0;
    }
    .loader {
      text-align: center;
    }
    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(140, 119, 46, 0.3);
      border-top-color: #8c772e;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 16px;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    .sub-text {
      font-size: 0.875rem;
      color: #9ca3af;
      margin-top: 8px;
    }
  </style>
</head>
<body>
  <div class="loader">
    <div class="spinner"></div>
    <p>Canvaにアップロード中...</p>
  </div>
</body>
</html>
`;

/**
 * Canva認証完了のpostMessageとlocalStorageを監視するhook
 */
export function useCanvaAuthListener(onAuthSuccess?: () => void) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // セキュリティ: 信頼できるオリジン (同一オリジン) からのメッセージのみ処理
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'CANVA_AUTH_SUCCESS') {
        // 認証ステータスを再取得
        queryClient.invalidateQueries({ queryKey: ['canva', 'authStatus'] });
        onAuthSuccess?.();
      }
    };

    // localStorageの変更を監視（同じウィンドウで認証した場合）
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'canva_auth_complete' && event.newValue) {
        // 認証ステータスを再取得
        queryClient.invalidateQueries({ queryKey: ['canva', 'authStatus'] });
        // localStorageをクリア
        localStorage.removeItem('canva_auth_complete');
        onAuthSuccess?.();
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [queryClient, onAuthSuccess]);
}

/**
 * Canva認証ステータスを取得するhook
 */
export function useCanvaAuthStatus() {
  return useQuery<CanvaAuthStatus>({
    queryKey: ['canva', 'authStatus'],
    queryFn: getCanvaAuthStatus,
    staleTime: 5 * 60 * 1000, // 5分間キャッシュ
    retry: false,
    // Canva 連携を出さないブランドではステータス照会自体を行わない
    enabled: brandConfig.features.canvaIntegration,
  });
}

/**
 * Canvaで編集するhook
 *
 * 画像の場合:
 * - ポップアップウィンドウでローディングを表示
 * - アップロード完了後にCanvaエディタを開く
 *
 * 動画の場合:
 * - モーダルでローディングを表示
 * - 完了後にCanvaへの導線ボタンを表示
 * - モーダルを閉じた場合は通知欄に通知を追加
 */
export function useEditInCanva() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<{
    materialId: string;
    isTemporary: boolean;
    targetWindow: Window | null;
    isVideo: boolean;
    thumbnailUrl?: string;
  } | null>(null);

  // 動画アップロードモーダルの状態
  const [videoUploadModal, setVideoUploadModal] = useState<VideoUploadModalState>({
    isOpen: false,
    isUploading: false,
    canvaUrl: null,
    thumbnailUrl: null,
    progress: 0,
  });

  // モーダルが開いているかどうかを追跡（アップロード完了時の判定用）
  const isModalOpenRef = useRef(false);
  isModalOpenRef.current = videoUploadModal.isOpen;

  // モーダルを閉じる
  const closeVideoUploadModal = useCallback(() => {
    setVideoUploadModal(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  // 認証完了時に自動的にCanvaエディタを開く
  useCanvaAuthListener(async () => {
    if (pendingRequest) {
      const { materialId, isTemporary, targetWindow, isVideo, thumbnailUrl } = pendingRequest;
      setPendingRequest(null);
      setIsAuthenticating(false);

      if (isVideo) {
        // 動画の場合：モーダルを開いてアップロード開始
        setVideoUploadModal({
          isOpen: true,
          isUploading: true,
          canvaUrl: null,
          thumbnailUrl: thumbnailUrl || null,
          progress: 0,
        });
        uploadVideoWithModal(materialId, isTemporary);
        return;
      }

      try {
        // 画像の場合：ポップアップで処理
        setIsUploading(true);
        const result = await uploadToCanvaAsync({
          material_id: materialId,
          is_temporary: isTemporary,
        });
        setIsUploading(false);

        if (result.edit_url && targetWindow && !targetWindow.closed) {
          targetWindow.location.href = result.edit_url;
        } else if (result.edit_url) {
          window.open(result.edit_url, '_blank');
        }
      } catch (error) {
        console.error('Failed to upload to Canva after auth:', error);
        setIsUploading(false);
        toast.error('Canvaへのアップロードに失敗しました');
        if (targetWindow && !targetWindow.closed) {
          targetWindow.close();
        }
      }
    }
  });

  // 動画をアップロード（モーダル表示版）
  const uploadVideoWithModal = useCallback(async (materialId: string, isTemporary: boolean) => {
    try {
      const result = await uploadToCanvaAsync(
        {
          material_id: materialId,
          is_temporary: isTemporary,
        },
        {
          onProgress: (job) => {
            // 進捗を更新
            if (job.progress !== undefined) {
              setVideoUploadModal(prev => ({
                ...prev,
                progress: job.progress ?? 0,
              }));
            }
          },
        }
      );

      if (result.canva_url) {
        // アップロード完了
        setVideoUploadModal(prev => ({
          ...prev,
          isUploading: false,
          canvaUrl: result.canva_url || null,
          progress: 100,
        }));

        // モーダルが閉じられている場合のみトーストで通知（ベルの通知欄には出さない）
        if (!isModalOpenRef.current) {
          toast.success('Canvaへの動画アップロードが完了しました');
        }
      }
    } catch (error) {
      console.error('Failed to upload video to Canva:', error);
      setVideoUploadModal(prev => ({
        ...prev,
        isOpen: false,
        isUploading: false,
      }));

      toast.error('Canvaへの動画アップロードに失敗しました');
    }
  }, [toast]);

  const editInCanvaWithAuth = useCallback(
    async (materialId: string, isTemporary: boolean = false, isVideo: boolean = false, thumbnailUrl?: string) => {
      // Safari のポップアップブロック対策: 認証や URL 取得が必要になり得るため、
      // ユーザー操作と同期的に新規タブを開いておき、後で location.href で遷移させる
      // (画像: ローディング表示にも使用 / 動画: 認証時のみ使い、不要なら閉じる)
      let targetWindow: Window | null = window.open('about:blank', '_blank');
      if (!isVideo && targetWindow) {
        targetWindow.document.write(LOADING_PAGE_HTML);
      }

      // まず認証ステータスをチェック
      let needsAuth = false;
      try {
        const status = await getCanvaAuthStatus();
        needsAuth = !status.connected;
      } catch {
        needsAuth = true;
      }

      if (needsAuth) {
        // 未認証の場合：認証フローを開始
        setIsAuthenticating(true);
        setPendingRequest({ materialId, isTemporary, targetWindow, isVideo, thumbnailUrl });

        try {
          // 動画は material_id を渡さない。渡すとコールバックが画像用のデザイン作成を走らせ
          // (Canva は画像アセットしか受け付けず 400)、認証完了ページに着かないため
          // 上の pendingRequest による動画アップロードも始まらない
          const { url } = await getCanvaAuthUrl(isVideo ? undefined : materialId, isTemporary);
          if (targetWindow && !targetWindow.closed) {
            targetWindow.location.href = url;
          } else {
            // ポップアップがブロックされた場合は同タブ遷移にフォールバック
            window.location.href = url;
          }
        } catch (authError) {
          console.error('Failed to get Canva auth URL:', authError);
          setIsAuthenticating(false);
          setPendingRequest(null);
          if (targetWindow && !targetWindow.closed) {
            targetWindow.close();
          }
        }
        return {} as CanvaEditResponse;
      }

      // 認証済みかつ動画の場合は事前に開いたタブは不要なので閉じる
      if (isVideo && targetWindow && !targetWindow.closed) {
        targetWindow.close();
        targetWindow = null;
      }

      // 認証済みの場合
      if (isVideo) {
        // 動画の場合：モーダルを開いてアップロード開始
        setVideoUploadModal({
          isOpen: true,
          isUploading: true,
          canvaUrl: null,
          thumbnailUrl: thumbnailUrl || null,
          progress: 0,
        });
        uploadVideoWithModal(materialId, isTemporary);
        return {} as CanvaEditResponse;
      }

      // 画像の場合：ポップアップで処理
      try {
        setIsUploading(true);
        const result = await uploadToCanvaAsync({
          material_id: materialId,
          is_temporary: isTemporary,
        });
        setIsUploading(false);

        if (result.edit_url) {
          if (targetWindow && !targetWindow.closed) {
            targetWindow.location.href = result.edit_url;
          } else {
            window.open(result.edit_url, '_blank');
          }
        } else {
          if (targetWindow && !targetWindow.closed) {
            targetWindow.close();
          }
        }

        queryClient.invalidateQueries({ queryKey: ['canva', 'authStatus'] });
        return {
          edit_url: result.edit_url || '',
          design_id: result.design_id || '',
        } as CanvaEditResponse;
      } catch (error: any) {
        setIsUploading(false);
        if (targetWindow && !targetWindow.closed) {
          targetWindow.close();
        }
        throw error;
      }
    },
    [queryClient, toast, uploadVideoWithModal]
  );

  return {
    editInCanva: editInCanvaWithAuth,
    isLoading: isAuthenticating || isUploading,
    error: null,
    isAuthenticating,
    isUploading,
    // 動画アップロードモーダル関連
    videoUploadModal,
    closeVideoUploadModal,
  };
}

/**
 * Canva接続を解除するhook
 */
export function useDisconnectCanva() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectCanva,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canva', 'authStatus'] });
    },
  });
}

/**
 * Canva認証を開始するhook
 */
export function useCanvaAuth() {
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const startAuth = useCallback(async () => {
    // Safari のポップアップブロック対策: ユーザー操作と同期的に新規タブを開き、
    // 認証 URL 取得後に location.href で遷移させる
    const targetWindow = window.open('about:blank', '_blank');
    setIsAuthenticating(true);
    try {
      const { url } = await getCanvaAuthUrl();
      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = url;
      } else {
        // ポップアップがブロックされた等で開けなかった場合は同タブ遷移にフォールバック
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to get Canva auth URL:', error);
      if (targetWindow && !targetWindow.closed) {
        targetWindow.close();
      }
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  return {
    startAuth,
    isAuthenticating,
  };
}
